import { ALL_LNGS_NAMESPACES } from '@ingenico/util-i18n-config';
import { Banner } from '@ingenico/react-ui/banner';
import { CoreContainer } from '@ingenico/react-ui/core-container';
import {
  DATAGRID_ROWS_PER_PAGE_OPTIONS,
  DataGridCore,
  HEADER_HEIGHT_TO_REMOVE_FROM_DATAGRID,
  ListViewContainer,
} from '@ingenico/react-ui/datagrid';
import { EmptyList } from '@ingenico/react-ui/notice-card';
import { LinearProgress } from '@mui/material';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { DeploymentTargetDevicesTabListFilters } from '../deployment-target-devices-tab-list-filters/DeploymentTargetDevicesTabListFilters.ctrl';
import type { DeploymentTargetDevicesTabInput } from './DeploymentTargetDevicesTab.model';
import { deploymentTargetDevicesInitialState } from './DeploymentTargetDevicesTab.util';
import { useDeploymentTargetDevicesTab } from './useDeploymentTargetDevicesTab.vm';

export function DeploymentTargetDevicesTab({
  logicalDeviceId,
  isOpenedFromLogicalDevice = false,
  isExposedComponent = false,
  isLoading,
}: DeploymentTargetDevicesTabInput): ReactNode {
  useTranslation([ALL_LNGS_NAMESPACES.DEPLOYMENT, ALL_LNGS_NAMESPACES.COMMON]);
  const {
    noRowsProps,
    gridProps,
    response,
    columns,
    shouldDisplayGrid,
    isFirstLoading,
    filterRefApi,
    fields,
    isLoadingQuickFilters,
    getLabelQuickFilterLabel,
    handleFilterChange,
    handleRowClick,
    overrideDefaultFilterTypes,
    checkAllowReset,
    advancedSearch,
    dateFilter,
    overrideResetFieldType,
    quickFiltersOptionListRequests,
    isQuickFilterLoading,
    handleRefresh,
    datagridErrorMessage,
    quickFiltersErrors,
  } = useDeploymentTargetDevicesTab({
    logicalDeviceId,
    isOpenedFromLogicalDevice,
    isExposedComponent,
  });

  return (
    <>
      {(isFirstLoading || isLoading) && <LinearProgress />}
      {response.isError && response.error && (
        <CoreContainer>
          <Banner message={datagridErrorMessage} />
        </CoreContainer>
      )}
      {shouldDisplayGrid && (
        <CoreContainer>
          <ListViewContainer>
            <DeploymentTargetDevicesTabListFilters
              advancedSearch={advancedSearch}
              checkAllowReset={checkAllowReset}
              fields={fields}
              filterRefApi={filterRefApi}
              getLabelQuickFilterLabel={getLabelQuickFilterLabel}
              handleFilterChange={handleFilterChange}
              isLoadingQuickFilters={isLoadingQuickFilters}
              overrideDefaultFilterTypes={overrideDefaultFilterTypes}
              dateFilter={dateFilter}
              overrideResetFieldType={overrideResetFieldType}
              quickFiltersOptionListRequests={quickFiltersOptionListRequests}
              isQuickFilterLoading={isQuickFilterLoading}
              isOpenedFromLogicalDevice={isOpenedFromLogicalDevice}
              onRefresh={handleRefresh}
              quickFiltersErrors={quickFiltersErrors}
            />
            {response?.data?.length === 0 && (
              <EmptyList
                image="empty-services"
                isBordered={false}
                messageLabel="emptyListOrPage.message"
                namespace={ALL_LNGS_NAMESPACES.COMMON}
                titleLabel="emptyListOrPage.title"
              />
            )}
            {response?.data?.length > 0 && (
              <DataGridCore
                apiRef={gridProps.ref}
                columnBufferPx={8}
                columns={columns}
                disableColumnMenu={false}
                rowSelection={false}
                filterMode="server"
                getRowId={gridProps.getRowId}
                headerHeightPixels={HEADER_HEIGHT_TO_REMOVE_FROM_DATAGRID}
                initialState={deploymentTargetDevicesInitialState}
                loading={response.isLoading}
                noRowsProps={noRowsProps}
                onPaginationModelChange={gridProps.handlePaginationModelChange}
                onRowClick={handleRowClick}
                onSortModelChange={gridProps.handleSortModelChange}
                paginationMode="server"
                rowCount={gridProps.total}
                rows={response.data}
                pageSizeOptions={DATAGRID_ROWS_PER_PAGE_OPTIONS}
                sortModel={gridProps.sortModel}
                sortingMode="server"
                columnVisibilityModel={gridProps.columnVisibilityModel}
                onColumnVisibilityModelChange={gridProps.handleChangeVisibleColumns}
              />
            )}
          </ListViewContainer>
        </CoreContainer>
      )}
    </>
  );
}
